import React, { useState } from 'react';
import { DataTable } from './components/DataTable';
import { secondClickOffers } from './data/mockData';
import { SecondClickOfferForm } from './forms/SecondClickOfferForm';
import { SecondClickOfferDetail } from './detail/SecondClickOfferDetail';
import { Plus, Copy, Archive, ExternalLink, Eye } from 'lucide-react';
export const SecondClickOffers: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const handleCreate = () => {
    setFormMode('create');
    setSelectedOffer(null);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleEdit = (offer: any) => {
    setFormMode('edit');
    setSelectedOffer(offer);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleClone = (offer: any) => {
    // Clone the offer with a new ID and slightly different name
    const clonedOffer = {
      ...offer,
      id: `sc_${Date.now()}`,
      name: `${offer.name} (Clone)`
    };
    // In a real app, you would save this to the backend
    console.log('Cloned offer:', clonedOffer);
    alert(`Cloned offer: ${clonedOffer.name}`);
  };
  const handleArchive = (offer: any) => {
    // In a real app, you would update the status in the backend
    console.log('Archived offer:', offer.id);
    alert(`Archived offer: ${offer.name}`);
  };
  const handleTestClick = (offer: any) => {
    // In a real app, this would simulate a second-click offer trigger
    alert(`Simulated second-click offer trigger: ${offer.name}`);
  };
  const handlePreview = (offer: any) => {
    setPreviewUrl(offer.creative.imageUrl);
  };
  const handleClosePreview = () => {
    setPreviewUrl(null);
  };
  const handleViewDetails = (offer: any) => {
    setSelectedOffer(offer);
    setIsDetailOpen(true);
    setIsFormOpen(false);
  };
  const handleFormSubmit = (formData: any) => {
    // In a real app, you would save this to the backend
    console.log('Form submitted:', formData);
    setIsFormOpen(false);
    alert(`${formMode === 'create' ? 'Created' : 'Updated'} offer: ${formData.name}`);
  };
  const handleFormCancel = () => {
    setIsFormOpen(false);
  };
  const handleDetailClose = () => {
    setIsDetailOpen(false);
  };
  const columns = [{
    id: 'status',
    header: 'Status',
    cell: (row: any) => <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${row.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-13534">
          {row.status === 'active' ? 'Active' : 'Inactive'}
        </span>
  }, {
    id: 'name',
    header: 'Offer Name',
    cell: (row: any) => <div className="font-medium text-purple-600 dark:text-purple-400" data-id="element-13535">
          {row.name}
        </div>
  }, {
    id: 'trigger',
    header: 'Trigger',
    cell: (row: any) => {
      const triggerMap: Record<string, string> = {
        purchase_success: 'Purchase Success',
        purchase_decline: 'Purchase Decline',
        exit_intent: 'Exit Intent',
        ritual_complete: 'Ritual Complete',
        content_unlock: 'Content Unlock',
        session_timeout: 'Session Timeout'
      };
      return triggerMap[row.triggerId] || row.triggerId;
    }
  }, {
    id: 'placement',
    header: 'Placement',
    cell: (row: any) => {
      const placementMap: Record<string, string> = {
        full_screen_modal: 'Full Screen Modal',
        bottom_sheet: 'Bottom Sheet',
        toast_with_cta: 'Toast with CTA',
        interstitial: 'Interstitial'
      };
      return placementMap[row.placementId] || row.placementId;
    }
  }, {
    id: 'segments',
    header: 'Segments',
    cell: (row: any) => <div className="flex flex-wrap gap-1" data-id="element-13536">
          {row.targeting.segments.map((segment: string) => <span key={segment} className="px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 rounded" data-id="element-13537">
              {segment.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </span>)}
          {row.targeting.segments.length > 2 && <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 rounded" data-id="element-13538">
              +{row.targeting.segments.length - 2} more
            </span>}
        </div>
  }, {
    id: 'type',
    header: 'Type',
    cell: (row: any) => <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${row.offerSource.type === 'internal' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'}`} data-id="element-13539">
          {row.offerSource.type === 'internal' ? 'Internal' : 'External'}
        </span>
  }, {
    id: 'product',
    header: 'Product/Partner',
    cell: (row: any) => <div data-id="element-13540">
          {row.offerSource.type === 'internal' ? row.offerSource.productName : row.offerSource.partnerName}
        </div>
  }, {
    id: 'price',
    header: 'Price',
    cell: (row: any) => <div data-id="element-13541">
          {row.offerSource.type === 'internal' ? <div className="flex items-center" data-id="element-13542">
              <span className="line-through text-gray-500 dark:text-gray-400 mr-2" data-id="element-13543">
                ${row.offerSource.regularPrice.toFixed(2)}
              </span>
              <span className="font-medium" data-id="element-13544">
                ${row.offerSource.discountedPrice.toFixed(2)}
              </span>
            </div> : <div data-id="element-13545">
              <span className="font-medium" data-id="element-13546">
                ${row.offerSource.payoutAmount.toFixed(2)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1" data-id="element-13547">
                ({row.offerSource.payoutModelId.toUpperCase()})
              </span>
            </div>}
        </div>
  }, {
    id: 'cr',
    header: 'CR',
    cell: (row: any) => <div data-id="element-13548">{(row.performance.cr * 100).toFixed(2)}%</div>
  }, {
    id: 'rpm',
    header: 'RPM',
    cell: (row: any) => <div data-id="element-13549">${row.performance.rpm.toFixed(2)}</div>
  }, {
    id: 'attachRate',
    header: 'Attach Rate',
    cell: (row: any) => <div data-id="element-13550">{(row.performance.attachRate * 100).toFixed(2)}%</div>
  }, {
    id: 'revenue',
    header: 'Revenue',
    cell: (row: any) => <div className="font-medium" data-id="element-13551">${row.performance.revenue.toFixed(2)}</div>
  }, {
    id: 'actions',
    header: 'Actions',
    cell: (row: any) => <div className="flex space-x-2" data-id="element-13552">
          <button onClick={e => {
        e.stopPropagation();
        handleEdit(row);
      }} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="Edit" data-id="element-13553">
            Edit
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleClone(row);
      }} className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300" title="Clone" data-id="element-13554">
            <Copy size={16} data-id="element-13555" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleArchive(row);
      }} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" title="Archive" data-id="element-13556">
            <Archive size={16} data-id="element-13557" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleTestClick(row);
      }} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" title="Test Trigger" data-id="element-13558">
            <ExternalLink size={16} data-id="element-13559" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handlePreview(row);
      }} className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300" title="Preview Creative" data-id="element-13560">
            <Eye size={16} data-id="element-13561" />
          </button>
        </div>,
    sortable: false
  }];
  return <div data-id="element-13562">
      <div className="flex justify-between items-center mb-6" data-id="element-13563">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-13564">
          Second-Click Offers
        </h1>
        <button onClick={handleCreate} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center" data-id="element-13565">
          <Plus size={16} className="mr-2" data-id="element-13566" />
          Create Second-Click Offer
        </button>
      </div>
      {!isFormOpen && !isDetailOpen && <DataTable data={secondClickOffers} columns={columns} title="Second-Click Offers" onRowClick={handleViewDetails} data-id="element-13567" />}
      {isFormOpen && <SecondClickOfferForm mode={formMode} initialData={selectedOffer} onSubmit={handleFormSubmit} onCancel={handleFormCancel} data-id="element-13568" />}
      {isDetailOpen && selectedOffer && <SecondClickOfferDetail offer={selectedOffer} onClose={handleDetailClose} onEdit={() => handleEdit(selectedOffer)} onClone={() => handleClone(selectedOffer)} onArchive={() => handleArchive(selectedOffer)} data-id="element-13569" />}
      {previewUrl && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" data-id="element-13570">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-hidden flex flex-col" data-id="element-13571">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-13572">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-13573">
                Creative Preview
              </h3>
              <button onClick={handleClosePreview} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-13574">
                &times;
              </button>
            </div>
            <div className="p-6 flex justify-center" data-id="element-13575">
              <img src={previewUrl} alt="Creative Preview" className="max-w-full max-h-[70vh] object-contain" data-id="element-13576" />
            </div>
          </div>
        </div>}
    </div>;
};